import { atom } from 'jotai';
import { createStandaloneToast } from '@chakra-ui/react';
import { chatV3Service } from '../../services/chatV3Service';
import { Conversation, Message } from '../../types';
import { atomWithManualStorage } from '../../utils/atomWithManualStorage';
import { navigateAtom } from './navigationAtoms';
import { shouldAutoScrollAtom } from './scrollControlAtoms';
import i18n from '../../lib/i18n';

// 统一的对话排序函数：按 updatedAt 降序排序（最新的在前）
const sortConversationsByUpdatedAt = (conversations: Conversation[]): Conversation[] => {
  return [...conversations].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};

// --- State Atoms (Using manual storage) ---
// 创建一个基础的 conversations atom，确保初始化时也应用排序
const baseConversationsAtom = atomWithManualStorage<Conversation[]>('v3_conversations', []);

// 包装 conversations atom，确保读取时总是排序的
export const conversationsAtom = atom(
  (get) => {
    const conversations = get(baseConversationsAtom);
    // 确保从存储读取的数据也是排序的
    return sortConversationsByUpdatedAt(conversations);
  },
  (get, set, update: Conversation[] | ((prev: Conversation[]) => Conversation[])) => {
    // 写入时也确保排序
    const newValue = typeof update === 'function' ? update(get(baseConversationsAtom)) : update;
    const sortedValue = sortConversationsByUpdatedAt(newValue);
    set(baseConversationsAtom, sortedValue);
  }
);
export const messagesAtom = atomWithManualStorage<Record<string, Message[]>>('v3_messages', {});
export const selectedConversationIdAtom = atomWithManualStorage<string | undefined>('v3_selectedConversationId', undefined);
export const messageSendingAtom = atom<boolean>(false);
export const conversationLoadingAtom = atom<boolean>(false); // For loading conversation list or details
export const messagesLoadingAtom = atom<boolean>(false); // Specifically for loading more messages
export const hasMoreMessagesAtom = atom<boolean>(false);

// --- Conversation List Pagination State ---
export const conversationPaginationAtom = atom<{
  offset: number;
  hasMore: boolean;
  loading: boolean;
}>({
  offset: 0,
  hasMore: true,
  loading: false,
});
// Store simple values directly
export type LastActiveChatView = 'list' | 'detail';
export const lastActiveChatViewAtom = atomWithManualStorage<LastActiveChatView>('v3_lastActiveView', 'detail');
export const chatScrollPositionsAtom = atomWithManualStorage<Record<string, number>>('v3_chatScrollPositions', {});
export const oldestMessageIdAtom = atomWithManualStorage<Record<string, string | null>>('v3_oldestMessageId', {});

export const newConverstaionMessageBufferAtom = atom<Message[]>([])

// --- Derived Atoms ---

// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

export const selectedConversationAtom = atom<Conversation | null>((get) => {
  const id = get(selectedConversationIdAtom);
  if (!id) return null;
  return get(conversationsAtom).find((c) => c.id === id) || null;
});

// 智能合并对话列表：处理刷新时的本地更新与服务器数据冲突
const mergeConversationsIntelligently = (
  existingConversations: Conversation[],
  serverConversations: Conversation[]
): Conversation[] => {
  // 创建服务器对话的映射，用于快速查找
  const serverConversationsMap = new Map(serverConversations.map(conv => [conv.id, conv]));

  // 合并逻辑：
  // 1. 对于服务器返回的对话，使用服务器数据（可能包含其他平台的更新）
  // 2. 对于本地存在但服务器不存在的对话，保留本地数据（可能是新创建的）
  const mergedMap = new Map<string, Conversation>();

  // 首先添加所有服务器对话
  serverConversations.forEach(serverConv => {
    mergedMap.set(serverConv.id, serverConv);
  });

  // 然后添加本地独有的对话（通常是新创建但还未同步的）
  existingConversations.forEach(localConv => {
    if (!serverConversationsMap.has(localConv.id)) {
      mergedMap.set(localConv.id, localConv);
    }
  });

  // 转换为数组并排序
  const mergedConversations = Array.from(mergedMap.values());
  return sortConversationsByUpdatedAt(mergedConversations);
};

// 更新指定对话的原子（如果不存在则创建）
export const updateConversationAtom = atom(
  null,
  (_get, set, payload: { conversationId: string; updates: Partial<Conversation>; createIfNotExists?: boolean }) => {
    const { conversationId, updates, createIfNotExists = true } = payload;

    set(conversationsAtom, (prev) => {
      const existingIndex = prev.findIndex(c => c.id === conversationId);

      if (existingIndex !== -1) {
        // 对话存在，更新它
        const updated = prev.map(conversation =>
          conversation.id === conversationId
            ? { ...conversation, ...updates }
            : conversation
        );
        // 使用统一的排序函数
        return sortConversationsByUpdatedAt(updated);
      } else if (createIfNotExists) {
        // 对话不存在且需要创建
        const newConversation: Conversation = {
          id: conversationId,
          title: i18n.t('v3/chat:newChat'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...updates
        };
        // 使用统一的排序函数
        return sortConversationsByUpdatedAt([newConversation, ...prev]);
      } else {
        // 对话不存在且不需要创建，返回原数组
        return prev;
      }
    });
  }
);

// 更新当前选中对话的简写原子
export const updateSelectedConversationAtom = atom(
  null,
  (get, set, updates: Partial<Conversation>) => {
    const selectedId = get(selectedConversationIdAtom);
    if (!selectedId) return;

    set(updateConversationAtom, {
      conversationId: selectedId,
      updates
    });
  }
);

// Read-only derived atom for current messages - returns base Message type
// 只读对象: 当前列表所有消息
export const currentMessagesAtom = atom<Message[]>((get) => {
  const selectedId = get(selectedConversationIdAtom);
  const allMessages = get(messagesAtom);
  const newConverstaionMessageBuffer = get(newConverstaionMessageBufferAtom)

  let res: Message[] = []
  // console.log(`currentMessagesAtom selectedId:`, selectedId);
  if (!selectedId || !(selectedId in allMessages)) {
    res = newConverstaionMessageBuffer
    // console.log('currentMessagesAtom newConverstaionMessageBuffer Updating:', JSON.stringify(res))
  } else {
    res = allMessages[selectedId];
    // console.log('currentMessagesAtom allMessages Updating:', JSON.stringify(res))
  }
  return res;
  // sort when write!!!! don't sort when read.
});



// 获得message数量
export const messageCountAtom = atom<number>((get) => {
  const messages = get(currentMessagesAtom);
  return messages.length
});


// --- Action Atoms ---

export const fetchConversationsAtom = atom(
  null,
  async (get, set, refresh = false) => {
    console.log("Fetching conversations...", refresh ? "(refresh)" : "(initial)");
    set(conversationLoadingAtom, true);

    // 如果是刷新，重置分页状态
    if (refresh) {
      set(conversationPaginationAtom, {
        offset: 0,
        hasMore: true,
        loading: false,
      });
    }

    try {
      const result = await chatV3Service.getConversations(10, 0); // 统一使用10条
      const newConversations = result.conversations;

      if (refresh) {
        // 刷新时智能合并：保留本地更新但尊重服务器排序
        const existingConversations = get(conversationsAtom);
        const mergedConversations = mergeConversationsIntelligently(existingConversations, newConversations);
        set(conversationsAtom, mergedConversations);
      } else {
        // 初始加载时直接设置
        set(conversationsAtom, newConversations);
      }

      set(conversationPaginationAtom, {
        offset: newConversations.length, // 已加载的数量
        hasMore: result.hasMore,
        loading: false,
      });
      console.log("Conversations fetched:", newConversations.length, "hasMore:", result.hasMore);
      return newConversations;
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
      toast({
        title: '获取对话列表失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return [];
    } finally {
      set(conversationLoadingAtom, false);
    }
  }
);

export const fetchConversationAtom = atom(
  null,
  async (_get, set, conversationId: string) => {
    if (!conversationId) return;
    set(conversationLoadingAtom, true);
    console.log(`Fetching conversation: ${conversationId}`);
    try {
      const result = await chatV3Service.getConversation(conversationId);
      const fetchedMessages = result.recentMessages || [];
      const conversation = result.conversation;

      // 清空新对话的消息缓冲区
      set(newConverstaionMessageBufferAtom, [])

      // 更新消息
      set(messagesAtom, (prev) => ({
        ...prev,
        [conversationId]: fetchedMessages,
      }));
      set(hasMoreMessagesAtom, result.hasMoreMessages);
      set(oldestMessageIdAtom, (prev) => ({
        ...prev,
        [conversationId]: fetchedMessages.length > 0 ? fetchedMessages[0].id : null
      }));

      // 使用updateConversationAtom更新conversation
      if (conversation) {
        set(updateConversationAtom, {
          conversationId,
          updates: conversation,
        });
      }


      // 成功加载对话后，启用自动滚动，确保用户进入对话时滚动到底部
      set(shouldAutoScrollAtom, true);

      console.log(`Conversation ${conversationId} fetched. Has more: ${result.hasMoreMessages}`);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      set(selectedConversationIdAtom, undefined); // 当访问Conversation的message列表失败时, 把当前conversionId设置成Null
      toast({
        title: '获取对话失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      set(conversationLoadingAtom, false);
    }
  }
);

export const loadMoreMessagesAtom = atom(
  null,
  async (get, set) => {
    const conversationId = get(selectedConversationIdAtom);
    if (!conversationId) return;

    const currentOldestId = get(oldestMessageIdAtom)[conversationId];
    if (!currentOldestId) {
      console.log("No known oldest message ID to load more.");
      set(hasMoreMessagesAtom, false);
      return;
    }
    console.log(`Loading more messages for ${conversationId} before ${currentOldestId}`);
    set(messagesLoadingAtom, true);
    try {
      const result = await chatV3Service.getMessages(conversationId, currentOldestId);
      const newMessages = result.messages || [];

      if (newMessages.length > 0) {
        set(messagesAtom, (prev) => {
          const existingMessages = prev[conversationId] || [];
          // 后端已经按时间戳升序排序，直接合并即可
          const combined = [...newMessages, ...existingMessages];
          const unique = Array.from(new Map(combined.map(m => [m.id, m])).values());
          return { ...prev, [conversationId]: unique };
        });
        set(oldestMessageIdAtom, (prev) => ({
          ...prev,
          [conversationId]: newMessages.length > 0 ? newMessages[0].id : currentOldestId
        }));
      }
      set(hasMoreMessagesAtom, result.hasMore);
      console.log(`Loaded ${newMessages.length} more messages. Has more: ${result.hasMore}`);

    } catch (error) {
      console.error('Failed to load more messages:', error);
      toast({
        title: '加载更多消息失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      set(messagesLoadingAtom, false);
    }
  }
);

export const loadMoreConversationsAtom = atom(
  null,
  async (get, set) => {
    const pagination = get(conversationPaginationAtom);

    // 如果已经在加载或没有更多数据，直接返回
    if (pagination.loading || !pagination.hasMore) {
      console.log("Load more conversations: already loading or no more data");
      return;
    }

    console.log(`Loading more conversations from offset ${pagination.offset}, current count: ${get(conversationsAtom).length}`);

    // 设置加载状态
    set(conversationPaginationAtom, {
      ...pagination,
      loading: true,
    });

    try {
      const result = await chatV3Service.getConversations(10, pagination.offset); // 每次加载10条
      const newConversations = result.conversations || [];

      // 合并新的对话到现有列表
      set(conversationsAtom, (prev) => {
        if (newConversations.length === 0) return prev;

        // 过滤出不重复的新对话
        const existingIds = new Set(prev.map(c => c.id));
        const uniqueNewConversations = newConversations.filter(c => !existingIds.has(c.id));

        if (uniqueNewConversations.length === 0) return prev;

        // 合并并重新排序，确保时间顺序正确
        // 这里不能简单追加，因为可能存在时间交叉的情况
        const combined = [...prev, ...uniqueNewConversations];
        return sortConversationsByUpdatedAt(combined);
      });

      // 更新分页状态
      set(conversationPaginationAtom, {
        offset: pagination.offset + newConversations.length,
        hasMore: result.hasMore,
        loading: false,
      });

      console.log(`Loaded ${newConversations.length} more conversations. Total now: ${get(conversationsAtom).length}. Has more: ${result.hasMore}. New offset: ${pagination.offset + newConversations.length}`);
      return newConversations;

    } catch (error) {
      console.error('Failed to load more conversations:', error);
      toast({
        title: '加载更多对话失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // 重置加载状态
      set(conversationPaginationAtom, {
        ...pagination,
        loading: false,
      });

      return [];
    }
  }
);

export const deleteConversationAtom = atom(
  null,
  async (get, set, conversationId: string): Promise<boolean> => {
    if (!conversationId) return false;
    console.log(`Deleting conversation: ${conversationId}`);
    const previousConversations = get(conversationsAtom);
    const previousMessages = get(messagesAtom);
    const previousSelectedId = get(selectedConversationIdAtom);

    // Optimistic UI update
    set(conversationsAtom, (prev) => prev.filter(c => c.id !== conversationId));

    // 删除对话时，更新分页状态
    set(conversationPaginationAtom, (prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - 1), // 减少一个对话的计数，但不能小于0
    }));

    // 修改点1: 使用delete操作符直接释放内存，而不是使用解构赋值
    set(messagesAtom, (prev) => {
      const newMessages = { ...prev };
      delete newMessages[conversationId]; // 直接删除对应的键值对，释放内存
      return newMessages;
    });

    // 修改点2: 如果删除的是当前选中的对话，直接导航到新建对话页面
    if (previousSelectedId === conversationId) {
      // 不再设置新的selectedConversationId，而是直接导航到新建对话页面
      set(navigateAtom, { path: '/v3/chat-detail/new', replace: true });
    }

    try {
      const result = await chatV3Service.deleteConversation(conversationId);
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete on server');
      }
      console.log(`Conversation ${conversationId} deleted successfully.`);

      // 修改点3: 删除chatScrollPositionsAtom中的相关记录
      // 这部分逻辑保持不变，因为它已经正确使用了delete模式的对象解构
      set(chatScrollPositionsAtom, (prev) => {
        const newPositions = { ...prev };
        delete newPositions[conversationId]; // 使用delete操作符删除
        return newPositions;
      });

      // 同样删除oldestMessageIdAtom中的相关记录
      set(oldestMessageIdAtom, (prev) => {
        const newOldestIds = { ...prev };
        delete newOldestIds[conversationId];
        return newOldestIds;
      });

      return true;
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Rollback UI on error
      set(conversationsAtom, previousConversations);
      set(messagesAtom, previousMessages);
      set(selectedConversationIdAtom, previousSelectedId);
      return false;
    }
  }
);